/**
 * HexClusterView — one group's title bar + its honeycomb of CameraHex tiles.
 * Positions are absolute canvas-space coords from layoutClusters().
 */
import CameraHex from './CameraHex';
import { TITLE_H, type LaidOutCluster, type PositionedHex } from './hexLayout';
import type { HexColorState } from './types';
import type { HexCamera, HexCounter } from '../../api/contracts';

interface HexClusterViewProps {
  cluster: LaidOutCluster<HexCamera>;
  selectedId: string | null;
  colorStateFor: (camera: HexCamera) => HexColorState;
  counterFor: (camera: HexCamera) => HexCounter | undefined;
  liveFor: (camera: HexCamera) => boolean;
  recordingFor: (camera: HexCamera) => boolean;
  onSelect: (hex: PositionedHex<HexCamera>) => void;
  onHoverStart: (hex: PositionedHex<HexCamera>) => void;
  onHoverEnd: () => void;
}

export default function HexClusterView({
  cluster,
  selectedId,
  colorStateFor,
  counterFor,
  liveFor,
  recordingFor,
  onSelect,
  onHoverStart,
  onHoverEnd,
}: HexClusterViewProps) {
  return (
    <>
      <div
        className="absolute flex select-none flex-col"
        style={{ left: cluster.originX, top: cluster.originY, width: cluster.width, height: TITLE_H }}
      >
        <div className="flex items-baseline gap-2">
          <span className="truncate text-sm font-semibold text-foreground">{cluster.title}</span>
          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            {cluster.hexes.length}
          </span>
        </div>
        {cluster.subtitle && (
          <span className="truncate text-[10px] uppercase tracking-wide text-muted-foreground">
            {cluster.subtitle}
          </span>
        )}
      </div>

      {cluster.hexes.map((hex) => (
        <CameraHex
          key={hex.item.id}
          name={hex.item.name}
          colorState={colorStateFor(hex.item)}
          counter={counterFor(hex.item)}
          live={liveFor(hex.item)}
          recording={recordingFor(hex.item)}
          x={hex.x}
          y={hex.y}
          selected={selectedId === hex.item.id}
          onClick={() => onSelect(hex)}
          onHoverStart={() => onHoverStart(hex)}
          onHoverEnd={onHoverEnd}
        />
      ))}
    </>
  );
}
